import { useEffect, useMemo, useState } from 'react';

import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import {
  type SchemaFieldValue,
  serializeSchemaValues,
} from '@/components/tenants/secrets/schema-form-fields';
import {
  useCurrentTenantID,
  useCurrentTenantSlug,
  useFindConnector,
  useFindConnectorFeature,
  useFindProvider,
  useFindProviderFeature,
  useListConnectorFeaturesWithProviderOptions,
  useListSecrets,
  useReconcileConnectorFeature,
  useUnbindConnectorFeatureCredential,
} from '@/hooks/scoped';
import {
  pathToConnectorFeatureRuns,
  pathToProviderFeatureEdit,
  pathToSettingsConnectorEdit,
} from '@/navigation/navigate';
import type { CredentialRequirement } from '@/types/core/provider';
import type { Secret } from '@/types/core/secret';
import type { FormSchema, SchemaProperty } from '@/types/shared/form-schema';
import { jsonSchemaToFormSchema } from '@/utils/json-schema-adapter';
import { isNilUUID, type UUID } from '@/utils/uuid';

/**
 * A credential slot the user must satisfy for a connector feature, derived from
 * the provider feature's declared credentials and resolved against the tenant's
 * secrets that match the slot's template.
 */
export type CredentialSlot = {
  /** Stable slot key used when binding the secret on submit. */
  name: string;
  /** Template the bound secret must conform to. */
  templateId: UUID;
  /** Human-friendly label for the slot (template name, falling back to the key). */
  label: string;
  /** Secrets selectable for this slot (already filtered to the slot template). */
  options: Array<{ value: string; label: string }>;
};

// ---------------------------------------------------------------------------
// Module-level pure helpers (no side effects, shared within this module)
// ---------------------------------------------------------------------------

/**
 * The backend serialises typed options as `{ $type: "...", content: { ...actual } }`.
 * When that wrapper is present, return the `content` object so callers work with
 * the actual schema-defined properties.
 */
function unwrapOptionsContent(
  options: Record<string, unknown>
): Record<string, unknown> {
  if (
    typeof options['$type'] === 'string' &&
    options['content'] != null &&
    typeof options['content'] === 'object' &&
    !Array.isArray(options['content'])
  ) {
    return options['content'] as Record<string, unknown>;
  }
  return options;
}

function normalizeSchemaFieldValue(
  value: unknown,
  fieldSchema?: SchemaProperty
): SchemaFieldValue {
  if (fieldSchema?.type === 'array' && Array.isArray(value)) {
    return value.map((v) => (v == null ? '' : String(v))).join('\n');
  }

  if (value != null && typeof value === 'object' && !Array.isArray(value)) {
    if (
      fieldSchema?.type === 'object' &&
      fieldSchema.properties &&
      Object.keys(fieldSchema.properties).length > 0
    ) {
      return Object.fromEntries(
        Object.entries(value).map(([key, nestedValue]) => [
          key,
          normalizeSchemaFieldValue(nestedValue, fieldSchema.properties?.[key]),
        ])
      );
    }

    return Object.fromEntries(
      Object.entries(value).map(([key, nestedValue]) => [key, nestedValue])
    );
  }

  if (Array.isArray(value)) {
    return value.map((v) => (v == null ? '' : String(v))).join('\n');
  }

  if (value == null) return '';
  return String(value);
}

function stableValueString(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'string') {
    const trimmedValue = value.trim();
    if (trimmedValue.startsWith('{') && trimmedValue.endsWith('}')) {
      try {
        return JSON.stringify(JSON.parse(trimmedValue));
      } catch {
        return value;
      }
    }
    return value;
  }
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

/**
 * Serialises option values when the schema has at least one property.
 * Returns undefined when there is nothing to send (schema absent or empty).
 */
function serializeOptionsPayload(
  schema: FormSchema | null,
  values: Record<string, SchemaFieldValue>
): Record<string, unknown> | undefined {
  if (!schema || Object.keys(schema.properties).length === 0) return undefined;
  return serializeSchemaValues(schema, values);
}

/**
 * Returns the credential slots a provider feature declares, keeping only slots
 * bound to a real (non-NIL) secret template — those are the ones that need a
 * secret selection.
 */
function deriveCredentialRequirements(
  credentials: ReadonlyArray<CredentialRequirement> | undefined
): CredentialRequirement[] {
  return (credentials ?? []).filter(
    (credential) => !!credential.templateId && !isNilUUID(credential.templateId)
  );
}

/**
 * Builds selectable secret options for a single credential slot, restricting to
 * secrets whose template matches the slot.
 */
function buildSlotOptions(
  templateId: UUID,
  secrets: ReadonlyArray<Secret>
): Array<{ value: string; label: string }> {
  return secrets
    .filter((s) => s.templateId === templateId && !!s.secretId)
    .map((s) => ({
      value: s.secretId,
      label: s.name ?? s.secretId ?? 'Unknown secret',
    }));
}

/**
 * Records the persisted selection for each slot (empty string when unbound),
 * used to detect form changes and to diff selections on submit.
 */
function existingSlotSelections(
  requirements: ReadonlyArray<CredentialRequirement>,
  secretIds: Readonly<Record<string, UUID>> | undefined
): Record<string, string> {
  const result: Record<string, string> = {};
  for (const requirement of requirements) {
    const sid = secretIds?.[requirement.name];
    result[requirement.name] = sid && !isNilUUID(sid) ? sid : '';
  }
  return result;
}

/**
 * Builds the additive slot-keyed secretIds patch to send. reconcile is additive (upsert), so the
 * patch carries only slots to bind/update: in edit mode the slots whose selection changed to a real
 * secret; in create mode every selected slot. Cleared slots are NOT expressed here — removing a
 * binding goes through the explicit unbind endpoint (see removeSecret). Returns undefined when there
 * is nothing to add or update.
 */
export function buildSecretIdsPatch(
  requirements: ReadonlyArray<CredentialRequirement>,
  selectedSecretIds: Record<string, string>,
  persisted: Record<string, string> | null
): Record<string, UUID> | undefined {
  const patch: Record<string, UUID> = {};
  for (const requirement of requirements) {
    const selected = selectedSecretIds[requirement.name] ?? '';
    if (!selected) continue; // empty selection is not a reconcile change; removal goes through unbind
    if (persisted && selected === persisted[requirement.name]) continue; // unchanged
    patch[requirement.name] = selected as UUID;
  }
  return Object.keys(patch).length > 0 ? patch : undefined;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export type ConnectorFeatureFormState = {
  // Data & loading
  isEditMode: boolean;
  isReadOnly: boolean;
  isLoading: boolean;
  isPending: boolean;

  // Display values derived from fetched data
  connectorName: string | undefined;
  featureName: string | undefined;
  providerName: string;
  featureDescription: string | undefined;
  providerFeatureLinkPath: string | null;
  featureRequirements: ReadonlyArray<string> | undefined;
  providerFeatureOptions: Array<{ value: string; label: string }>;
  activeProviderFeatureName: string | undefined;

  // Form field state
  selectedFeatureId: string;
  /** Selected secret id per credential slot name. */
  selectedSecretIds: Record<string, string>;
  /** Slot names that currently have a persisted binding (drives the Remove action). */
  boundSlotNames: ReadonlySet<string>;
  /** True while an explicit credential unbind is in flight. */
  isUnbinding: boolean;
  isEnabled: boolean;
  optionValues: Record<string, SchemaFieldValue>;
  adaptedOptionsSchema: FormSchema | null;
  /** Credential slots the user must satisfy (one picker each). */
  credentialSlots: CredentialSlot[];
  requiresSecret: boolean;
  /** True once every required credential slot has a selection. */
  hasAllRequiredSecrets: boolean;
  hasFormChanges: boolean;

  // Feedback
  errorMessage: string | null;
  successMessage: string | null;

  // Requirements editor
  featureReqList: string[];

  // Tenant context (for child modals)
  tenantId: UUID;

  // Actions
  setSelectedFeatureId: (v: string) => void;
  /** Select a secret for a specific credential slot. */
  selectSecret: (slotName: string, secretId: string) => void;
  setIsEnabled: (v: boolean) => void;
  updateOptionValue: (field: string, value: SchemaFieldValue) => void;
  clearErrorMessage: () => void;
  clearSuccessMessage: () => void;
  handleSubmit: () => Promise<void>;
  handleCancel: () => void;
  handleAddReq: (value: string) => void;
  handleRemoveReq: (index: number) => void;
  /** Explicitly unbind the secret currently bound to a credential slot. */
  removeSecret: (slotName: string) => Promise<void>;
};

export function useConnectorFeatureForm(
  connectorId: UUID,
  featureId?: UUID,
  options?: { onSaved?: () => void; onCancel?: () => void }
): ConnectorFeatureFormState {
  const navigate = useNavigate();
  const tenantId = useCurrentTenantID();
  const tenantSlug = useCurrentTenantSlug();

  const isEditMode = !!featureId;

  // ---------------------------------------------------------------------------
  // Local state
  // ---------------------------------------------------------------------------
  const [selectedFeatureId, setSelectedFeatureId] = useState<string>(
    featureId ?? ''
  );
  const [selectedSecretIds, setSelectedSecretIds] = useState<
    Record<string, string>
  >({});
  const [touchedSlots, setTouchedSlots] = useState<Set<string>>(new Set());
  const [isEnabled, setIsEnabled] = useState(true);
  const [optionValues, setOptionValues] = useState<
    Record<string, SchemaFieldValue>
  >({});
  const [featureReqList, setFeatureReqList] = useState<string[]>([]);

  // ---------------------------------------------------------------------------
  // Queries
  // ---------------------------------------------------------------------------
  const { data: connector, isLoading: isLoadingConnector } = useFindConnector(
    tenantId,
    connectorId
  );
  const providerId = connector?.providerId;
  const { data: provider } = useFindProvider(tenantId, providerId);

  const {
    data: connectorFeaturesWithProviderOptions,
    isLoading: isLoadingConnectorFeaturesWithProviderOptions,
  } = useListConnectorFeaturesWithProviderOptions(tenantId, connectorId, {
    enabled: !isEditMode,
  });

  const { data: existingFeature, isLoading: isLoadingFeature } =
    useFindConnectorFeature(tenantId, connectorId, featureId);

  const { data: providerFeature, isLoading: isLoadingProviderFeature } =
    useFindProviderFeature(tenantId, providerId, featureId);

  const { data: secretsData } = useListSecrets(tenantId);

  // ---------------------------------------------------------------------------
  // Derived data
  // ---------------------------------------------------------------------------
  const combinedFeatures = useMemo(
    () => connectorFeaturesWithProviderOptions ?? [],
    [connectorFeaturesWithProviderOptions]
  );

  const connectorFeatures = useMemo(
    () => combinedFeatures.map((item) => item.connectorFeature),
    [combinedFeatures]
  );

  const providerFeatureOptions = useMemo(
    () =>
      combinedFeatures
        .filter((item) => !item.connectorFeature.status)
        .map((item) => ({
          value: item.connectorFeature.featureId ?? '',
          label:
            item.providerFeature?.name ??
            item.connectorFeature.name ??
            item.connectorFeature.featureId ??
            'Unknown feature',
        })),
    [combinedFeatures]
  );

  const activeFeatureId = isEditMode ? featureId : selectedFeatureId;

  const activeCombinedFeature = useMemo(
    () =>
      combinedFeatures.find(
        (item) => item.connectorFeature.featureId === activeFeatureId
      ) ?? null,
    [activeFeatureId, combinedFeatures]
  );

  const activeProviderFeature = useMemo(() => {
    if (isEditMode) return providerFeature ?? null;
    return activeCombinedFeature?.providerFeature ?? null;
  }, [activeCombinedFeature, isEditMode, providerFeature]);

  const secrets = useMemo(() => secretsData ?? [], [secretsData]);

  // Credential slots the active feature declares (each needs its own secret).
  const credentialRequirements = useMemo(
    () => deriveCredentialRequirements(activeProviderFeature?.credentials),
    [activeProviderFeature?.credentials]
  );

  const credentialSlots = useMemo<CredentialSlot[]>(
    () =>
      credentialRequirements.map((requirement) => ({
        name: requirement.name,
        templateId: requirement.templateId as UUID,
        label: requirement.templateName ?? requirement.name,
        options: buildSlotOptions(requirement.templateId as UUID, secrets),
      })),
    [credentialRequirements, secrets]
  );

  const requiresSecret = credentialSlots.length > 0;

  const hasAllRequiredSecrets = credentialSlots.every((slot) => {
    const selected = selectedSecretIds[slot.name];
    return !!selected && !isNilUUID(selected as UUID);
  });

  const adaptedOptionsSchema = useMemo(
    () => jsonSchemaToFormSchema(activeProviderFeature?.optionsSchema),
    [activeProviderFeature?.optionsSchema]
  );

  const isReadOnly = connector?.is_system ?? false;

  const isLoading = [
    isLoadingConnector,
    !isEditMode && isLoadingConnectorFeaturesWithProviderOptions,
    isEditMode && isLoadingFeature,
    isEditMode && isLoadingProviderFeature,
  ].some(Boolean);

  const featureName =
    activeProviderFeature?.name ??
    existingFeature?.name ??
    connectorFeatures.find((f) => f.featureId === featureId)?.name ??
    featureId;

  const providerName =
    provider?.name ?? connector?.providerName ?? 'Unknown provider';

  const featureDescription =
    activeProviderFeature?.description ??
    activeCombinedFeature?.connectorFeature.description;

  const providerFeatureLinkPath =
    tenantSlug && connector?.providerId && activeFeatureId
      ? pathToProviderFeatureEdit(
          tenantSlug,
          connector.providerId,
          activeFeatureId as UUID
        )
      : null;

  const featureRequirements =
    activeProviderFeature?.requirements ??
    activeCombinedFeature?.connectorFeature.requirements;

  const hasFormChanges = useMemo(() => {
    if (!isEditMode || !existingFeature) return true;
    const persistedSelections = existingSlotSelections(
      credentialRequirements,
      existingFeature.secretIds
    );
    for (const slot of credentialRequirements) {
      const selected = selectedSecretIds[slot.name] ?? '';
      if (selected !== persistedSelections[slot.name]) return true;
    }
    if (isEnabled !== (existingFeature.status ?? true)) return true;
    if (adaptedOptionsSchema) {
      const savedSrc = existingFeature.options
        ? unwrapOptionsContent(existingFeature.options)
        : undefined;
      for (const key of Object.keys(adaptedOptionsSchema.properties)) {
        if (
          stableValueString(optionValues[key]) !==
          stableValueString(savedSrc?.[key])
        ) {
          return true;
        }
      }
    }
    return false;
  }, [
    adaptedOptionsSchema,
    credentialRequirements,
    existingFeature,
    isEditMode,
    isEnabled,
    optionValues,
    selectedSecretIds,
  ]);

  // ---------------------------------------------------------------------------
  // Mutations
  // ---------------------------------------------------------------------------
  const reconcileFeatureMutation = useReconcileConnectorFeature(
    tenantId,
    connectorId
  );
  const unbindCredentialMutation = useUnbindConnectorFeatureCredential(
    tenantId,
    connectorId
  );

  const isPending =
    reconcileFeatureMutation.isPending || unbindCredentialMutation.isPending;

  // Slots currently bound on the persisted feature (real bindings only; the
  // adapter already drops NIL placeholders). Drives the per-slot Remove action.
  const boundSlotNames = useMemo(
    () => new Set(Object.keys(existingFeature?.secretIds ?? {})),
    [existingFeature?.secretIds]
  );

  // ---------------------------------------------------------------------------
  // Effects
  // ---------------------------------------------------------------------------

  // Reset secret selection when the user picks a different provider feature (create mode).
  useEffect(() => {
    if (!isEditMode) {
      setSelectedSecretIds({});
      setTouchedSlots(new Set());
    }
  }, [selectedFeatureId, isEditMode]);

  // Prefill form from the persisted feature record on load (edit mode).
  useEffect(() => {
    if (!existingFeature) return;
    setSelectedSecretIds(
      existingSlotSelections(credentialRequirements, existingFeature.secretIds)
    );
    setTouchedSlots(new Set());
    setIsEnabled(existingFeature.status ?? true);

    const providerReqs = new Set(activeProviderFeature?.requirements ?? []);
    setFeatureReqList(
      existingFeature.requirements
        ? existingFeature.requirements.filter((r) => !providerReqs.has(r))
        : []
    );

    if (existingFeature.options) {
      const src = unwrapOptionsContent(existingFeature.options);
      setOptionValues(
        Object.fromEntries(
          Object.entries(src).map(([k, v]) => [
            k,
            normalizeSchemaFieldValue(v, adaptedOptionsSchema?.properties[k]),
          ])
        )
      );
    }
    // oxlint-disable-next-line react/exhaustive-deps
  }, [
    adaptedOptionsSchema,
    existingFeature?.secretIds,
    existingFeature?.options,
    existingFeature?.requirements,
  ]);

  // Re-filter user requirements if provider feature data arrives after the init effect.
  // This guards against the data-loading race where providerReqs was empty on first run.
  useEffect(() => {
    if (!activeProviderFeature?.requirements?.length) return;
    const providerReqSet = new Set(activeProviderFeature.requirements);
    setFeatureReqList((prev) => prev.filter((r) => !providerReqSet.has(r)));
  }, [activeProviderFeature?.requirements]);

  // Auto-select a slot's secret when it narrows to exactly one option and the
  // user has not made a manual choice for that slot (create mode only).
  useEffect(() => {
    if (isEditMode) return;
    const autoSelected: Record<string, string> = {};
    for (const slot of credentialSlots) {
      if (touchedSlots.has(slot.name)) continue;
      if (selectedSecretIds[slot.name]) continue;
      if (slot.options.length === 1) {
        autoSelected[slot.name] = slot.options[0].value;
      }
    }
    if (Object.keys(autoSelected).length > 0) {
      setSelectedSecretIds((prev) => ({ ...prev, ...autoSelected }));
    }
  }, [credentialSlots, touchedSlots, isEditMode, selectedSecretIds]);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const autoSaveRequirements = async (newList: string[]) => {
    if (!isEditMode || !featureId || isReadOnly) return;
    const providerReqArr = activeProviderFeature?.requirements
      ? [...activeProviderFeature.requirements]
      : [];
    const providerReqSet = new Set(providerReqArr);
    const mergedReqs = [
      ...providerReqArr,
      ...newList.filter((r) => !providerReqSet.has(r)),
    ];
    try {
      await reconcileFeatureMutation.mutateAsync({
        featureId: featureId as UUID,
        requirements: mergedReqs,
      });
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Failed to save requirements.'
      );
    }
  };

  const handleAddReq = (value: string) => {
    if (!value || featureReqList.includes(value)) return;
    const newList = [...featureReqList, value];
    setFeatureReqList(newList);
    if (isEditMode) void autoSaveRequirements(newList);
  };

  const handleRemoveReq = (index: number) => {
    const newList = featureReqList.filter((_, i) => i !== index);
    setFeatureReqList(newList);
    if (isEditMode) void autoSaveRequirements(newList);
  };

  const handleCancel = () => {
    if (options?.onCancel) {
      options.onCancel();
    } else {
      navigate(
        `${pathToSettingsConnectorEdit(tenantSlug, connectorId)}/features`
      );
    }
  };

  // Explicitly unbind a single credential slot. reconcile is additive and never
  // drops a binding, so removal must call the dedicated unbind endpoint.
  const removeSecret = async (slotName: string) => {
    if (!isEditMode || !featureId || isReadOnly) return;
    try {
      await unbindCredentialMutation.mutateAsync({
        featureId: featureId as UUID,
        slotName,
      });
      setTouchedSlots((prev) => new Set(prev).add(slotName));
      setSelectedSecretIds((prev) => ({ ...prev, [slotName]: '' }));
      toast.success('Credential removed');
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Failed to remove credential.'
      );
    }
  };

  const handleSubmit = async () => {
    if (isReadOnly) return;

    const targetFeatureId = (
      isEditMode ? featureId : selectedFeatureId
    ) as UUID;
    if (!targetFeatureId) {
      toast.error('Please select a provider feature');
      return;
    }
    if (requiresSecret && !hasAllRequiredSecrets) {
      toast.error('Select a secret for every required credential');
      return;
    }

    try {
      if (isEditMode) {
        const persisted = existingSlotSelections(
          credentialRequirements,
          existingFeature?.secretIds
        );
        const options = serializeOptionsPayload(
          adaptedOptionsSchema,
          optionValues
        );
        const secretIds = buildSecretIdsPatch(
          credentialRequirements,
          selectedSecretIds,
          persisted
        );
        const enabledChanged = isEnabled !== (existingFeature?.status ?? true);
        await reconcileFeatureMutation.mutateAsync({
          featureId: targetFeatureId,
          ...(options !== undefined && { options }),
          ...(secretIds !== undefined && { secretIds }),
          ...(enabledChanged && { enabled: isEnabled }),
        });
      } else {
        const secretIds = buildSecretIdsPatch(
          credentialRequirements,
          selectedSecretIds,
          null
        );
        const options = serializeOptionsPayload(
          adaptedOptionsSchema,
          optionValues
        );
        const requirements = activeProviderFeature?.requirements?.length
          ? [...activeProviderFeature.requirements]
          : undefined;
        await reconcileFeatureMutation.mutateAsync({
          featureId: targetFeatureId,
          enabled: true,
          ...(secretIds !== undefined && { secretIds }),
          ...(options !== undefined && { options }),
          ...(requirements !== undefined && { requirements }),
        });
      }

      toast.success(
        isEditMode
          ? 'Feature updated successfully'
          : 'Feature added successfully'
      );

      if (options?.onSaved) {
        setTimeout(() => options.onSaved!(), 600);
      } else {
        setTimeout(() => {
          navigate(
            isEditMode && featureId && tenantSlug
              ? pathToConnectorFeatureRuns(tenantSlug, connectorId, featureId)
              : `${pathToSettingsConnectorEdit(tenantSlug, connectorId)}/features`
          );
        }, 1000);
      }
    } catch (error) {
      console.error('Failed to save connector feature:', error);
      toast.error(
        error instanceof Error
          ? error.message
          : 'Failed to save connector feature. Please try again.'
      );
    }
  };

  // ---------------------------------------------------------------------------
  // Return
  // ---------------------------------------------------------------------------
  return {
    isEditMode,
    isReadOnly,
    isLoading,
    isPending,

    connectorName: connector?.name,
    featureName,
    providerName,
    featureDescription,
    providerFeatureLinkPath,
    featureRequirements,
    providerFeatureOptions,
    activeProviderFeatureName: activeProviderFeature?.name,

    selectedFeatureId,
    selectedSecretIds,
    boundSlotNames,
    isUnbinding: unbindCredentialMutation.isPending,
    isEnabled,
    optionValues,
    adaptedOptionsSchema,
    credentialSlots,
    requiresSecret,
    hasAllRequiredSecrets,
    hasFormChanges,

    errorMessage: null,
    successMessage: null,

    // Subtract provider requirements at the return boundary so display is always
    // correct regardless of which query resolves first.
    featureReqList: featureRequirements?.length
      ? featureReqList.filter((r) => !featureRequirements.includes(r))
      : featureReqList,

    tenantId,

    setSelectedFeatureId,
    selectSecret: (slotName: string, secretId: string) => {
      setTouchedSlots((prev) => new Set(prev).add(slotName));
      setSelectedSecretIds((prev) => ({ ...prev, [slotName]: secretId }));
    },
    setIsEnabled,
    updateOptionValue: (field: string, value: SchemaFieldValue) =>
      setOptionValues((prev) => ({ ...prev, [field]: value })),
    clearErrorMessage: () => {},
    clearSuccessMessage: () => {},
    handleSubmit,
    handleCancel,
    handleAddReq,
    handleRemoveReq,
    removeSecret,
  };
}
